import { AuthenticatedEvent, ExitAction, SessionExpiredEvent } from './messages'
import type {
  AddToCalendarDelegate,
  AuthTokenDelegate,
  CreateExternalUrlHandleOptions,
  DocumentDownloadDelegate,
  ExternalUrlDelegate,
  ExternalUrlOpenHandle,
  LifecycleDelegate,
  PlatformDelegate,
} from './types'

class DefaultExternalUrlDelegate implements ExternalUrlDelegate {
  createHandle(
    options?: CreateExternalUrlHandleOptions,
  ): ExternalUrlOpenHandle {
    if (options?.sameWindow) {
      return {
        open(url: string) {
          window.location.href = url
        },
        abort() {
          // no-op
        },
      }
    }

    const newWindow = window.open('about:blank', '_blank')
    return {
      open(url: string) {
        if (newWindow) {
          newWindow.location.href = url
        }
      },
      abort() {
        newWindow?.close()
      },
    }
  }
}

class DefaultDocumentDownloadDelegate implements DocumentDownloadDelegate {
  download(url: string, preview: boolean): void {
    if (preview) {
      window.open(url, '_blank')
      return
    }

    const link = document.createElement('a')
    link.href = url
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

class DefaultAddToCalendarDelegate implements AddToCalendarDelegate {
  addToCalendar(icsFile: File): void {
    const url = URL.createObjectURL(icsFile)
    const link = document.createElement('a')
    link.href = url
    link.download = 'calendar.ics'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}

class DefaultLifecycleDelegate implements LifecycleDelegate {
  get supportsDelegatedExit() {
    return window.parent !== window.self
  }

  notifyAuthenticated(): void {
    window.parent.postMessage(AuthenticatedEvent(), '*')
  }

  notifySessionExpired(): void {
    window.parent.postMessage(SessionExpiredEvent(), '*')
  }

  requestExit(returnUrl: string): void {
    // When running in an iframe, consumers may be listening for the exit
    // action via the `message` event.
    window.parent.postMessage(ExitAction(), '*')
    // Give the consumer (if any) time to handle the message, but redirect
    // anyway in case nobody is listening.
    setTimeout(() => {
      window.location.href = returnUrl
    }, 10)
  }
}

class DefaultAuthTokenDelegate implements AuthTokenDelegate {
  getToken(): Promise<string | null> {
    return Promise.resolve(null)
  }
}

/**
 * Default delegate for the web.
 */
export class DefaultPlatformDelegate implements PlatformDelegate {
  private readonly _externalUrlDelegate = new DefaultExternalUrlDelegate()
  private readonly _documentDownloadDelegate =
    new DefaultDocumentDownloadDelegate()

  private readonly _addToCalendarDelegate = new DefaultAddToCalendarDelegate()
  private readonly _lifecycleDelegate = new DefaultLifecycleDelegate()
  private readonly _authTokenDelegate = new DefaultAuthTokenDelegate()

  getExternalUrlDelegate = () => this._externalUrlDelegate
  getDocumentDownloadDelegate = () => this._documentDownloadDelegate
  getAddToCalendarDelegate = () => this._addToCalendarDelegate
  getLifecycleDelegate = () => this._lifecycleDelegate
  getAuthTokenDelegate = () => this._authTokenDelegate
}
